import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import Layout from "@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ScrollArea } from "@/components/ui/scroll-area";
import DeleteConfirmDialog from "@/components/DeleteConfirmDialog";
import { 
  MessageSquare, 
  Plus, 
  ChevronRight, 
  Megaphone,
  Heart,
  BookOpen,
  Sparkles,
  User,
  Trash2
} from "lucide-react";
import { format } from "date-fns";
import { toast } from "react-hot-toast";

const CATEGORIES = [
  { value: 'announcements', label: 'Announcements', icon: Megaphone, color: 'bg-slate-700' },
  { value: 'prayer_requests', label: 'Prayer Requests', icon: Heart, color: 'bg-amber-600' },
  { value: 'bible_verses', label: 'Bible Verses', icon: BookOpen, color: 'bg-teal-600' }
];

export default function Forum({ user }) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewPost, setShowNewPost] = useState(false);
  const [aiSuggestion, setAiSuggestion] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [postToDelete, setPostToDelete] = useState(null);
  
  const currentTab = searchParams.get('tab') || 'all';
  
  // New post form
  const [newPost, setNewPost] = useState({
    category: 'prayer_requests',
    title: '',
    content: ''
  });
  const [verseSearch, setVerseSearch] = useState('');
  const [verseSearchResult, setVerseSearchResult] = useState('');
  const [verseSearchLoading, setVerseSearchLoading] = useState(false);

  useEffect(() => {
    fetchPosts();
  }, [currentTab]);

  const fetchPosts = async () => {
    try {
      const category = currentTab !== 'all' ? currentTab : null;
      const url = category ? `${API}/forum/posts?category=${category}` : `${API}/forum/posts`;
      const response = await axios.get(url);
      setPosts(response.data);
    } catch (error) {
      console.error("Error fetching posts:", error);
    } finally {
      setLoading(false);
    }
  };

  const createPost = async () => {
    if (!newPost.title.trim() || !newPost.content.trim()) {
      toast.error("Please fill in all fields");
      return;
    }

    try {
      await axios.post(`${API}/forum/posts`, newPost);
      toast.success("Post created!");
      setShowNewPost(false);
      setNewPost({ category: 'prayer_requests', title: '', content: '' });
      fetchPosts();
    } catch (error) {
      console.error("Error creating post:", error);
      toast.error("Failed to create post");
    }
  };

  const deletePost = async (e, postId) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    setPostToDelete(postId);
    setDeleteDialogOpen(true);
  };

  const confirmDeletePost = async () => {
    if (!postToDelete) return;
    
    try {
      await axios.delete(`${API}/forum/posts/${postToDelete}`);
      toast.success("Post deleted");
      fetchPosts();
    } catch (error) {
      console.error("Error deleting post:", error);
      toast.error("Failed to delete post");
    } finally {
      setPostToDelete(null);
      setDeleteDialogOpen(false);
    }
  };

  const canDeletePost = (post) => {
    return user.role === 'teacher' || post.author_id === user.user_id;
  };

  const getBibleVerse = async () => {
    setAiLoading(true);
    try {
      const response = await axios.post(`${API}/ai/bible-verse`, {
        prompt: "Suggest an encouraging Bible verse for someone going through a difficult time. Include the verse reference and a brief explanation."
      });
      setAiSuggestion(response.data.suggestion);
    } catch (error) {
      console.error("Error getting verse:", error);
      toast.error("Failed to get verse suggestion");
    } finally {
      setAiLoading(false);
    }
  };

  const searchBibleVerse = async () => {
    if (!verseSearch.trim()) {
      toast.error("Please enter a topic or keyword to search");
      return;
    }
    setVerseSearchLoading(true);
    try {
      const response = await axios.post(`${API}/ai/bible-verse`, {
        prompt: `Find Bible verses about: ${verseSearch}. Please provide 1-2 relevant verses with their full text and references. Format each verse on a new line.`
      });
      setVerseSearchResult(response.data.suggestion);
    } catch (error) {
      console.error("Error searching verse:", error);
      toast.error("Failed to search for verses");
    } finally {
      setVerseSearchLoading(false);
    }
  };

  const insertVerseToPost = () => {
    if (verseSearchResult) {
      const newContent = newPost.content 
        ? `${newPost.content}\n\n${verseSearchResult}`
        : verseSearchResult;
      setNewPost({...newPost, content: newContent});
      setVerseSearchResult('');
      setVerseSearch('');
      toast.success("Verse added to your post");
    }
  };

  const getCategoryConfig = (category) => {
    return CATEGORIES.find(c => c.value === category) || CATEGORIES[0];
  };

  if (loading) {
    return (
      <Layout user={user}>
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-4 border-slate-300 border-t-amber-500 rounded-full animate-spin"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user}>
      <div data-testid="forum-page" className="space-y-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="font-serif text-2xl md:text-3xl font-bold text-slate-900">
              Community Forum
            </h1>
            <p className="text-slate-600 mt-1">
              Share, pray, and grow together with your community
            </p>
          </div>
          
          <div className="flex gap-3">
            {/* AI Verse Button */}
            <Dialog>
              <DialogTrigger asChild>
                <Button 
                  variant="outline"
                  className="rounded-full border-amber-300 text-amber-700 hover:bg-amber-50"
                >
                  <Sparkles className="w-4 h-4 mr-2" />
                  Get Verse
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-lg">
                <DialogHeader>
                  <DialogTitle className="font-serif flex items-center">
                    <Sparkles className="w-5 h-5 mr-2 text-amber-600" />
                    AI Bible Verse Suggestion
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-4 pt-4">
                  {aiSuggestion ? (
                    <div className="p-4 bg-amber-50 rounded-xl border border-amber-200">
                      <p className="text-slate-800 whitespace-pre-wrap">{aiSuggestion}</p>
                    </div>
                  ) : (
                    <p className="text-slate-600 text-center py-4">
                      Click below to get an AI-suggested Bible verse for encouragement
                    </p>
                  )}
                  <Button
                    data-testid="ai-verse-btn"
                    onClick={getBibleVerse}
                    disabled={aiLoading}
                    className="w-full bg-amber-600 hover:bg-amber-700 rounded-full"
                  >
                    {aiLoading ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2"></div>
                        Generating...
                      </>
                    ) : (
                      <>
                        <Sparkles className="w-4 h-4 mr-2" />
                        {aiSuggestion ? 'Get Another Verse' : 'Get Verse Suggestion'}
                      </>
                    )}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>

            {/* New Post Button */}
            <Dialog open={showNewPost} onOpenChange={(open) => {
              setShowNewPost(open);
              if (!open) {
                setVerseSearch('');
                setVerseSearchResult('');
              }
            }}>
              <DialogTrigger asChild>
                <Button 
                  data-testid="new-post-btn"
                  className="bg-slate-800 hover:bg-slate-900 rounded-full shadow-lg shadow-slate-800/20"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  New Post
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="font-serif">Create New Post</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 pt-4">
                  <div>
                    <label className="text-sm font-medium text-slate-700">Category</label>
                    <Select 
                      value={newPost.category} 
                      onValueChange={(v) => setNewPost({...newPost, category: v})}
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {CATEGORIES.map((cat) => (
                          <SelectItem 
                            key={cat.value} 
                            value={cat.value}
                            disabled={cat.value === 'announcements' && user.role !== 'teacher'}
                          >
                            <div className="flex items-center">
                              <cat.icon className="w-4 h-4 mr-2" />
                              {cat.label}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-700">Title</label>
                    <Input
                      data-testid="post-title-input"
                      value={newPost.title}
                      onChange={(e) => setNewPost({...newPost, title: e.target.value})}
                      placeholder="Post title"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-700">Content</label>
                    <Textarea
                      data-testid="post-content-input"
                      value={newPost.content}
                      onChange={(e) => setNewPost({...newPost, content: e.target.value})}
                      placeholder="Share your thoughts..."
                      className="mt-1 min-h-[150px]"
                    />
                  </div>

                  {/* Bible Verse Search Section */}
                  <div className="border-t border-slate-200 pt-4">
                    <label className="text-sm font-medium text-slate-700 flex items-center gap-2">
                      <BookOpen className="w-4 h-4 text-teal-600" />
                      Search Bible Verse
                    </label>
                    <p className="text-xs text-slate-500 mt-1 mb-2">
                      Search for a verse by topic or keyword to add to your post
                    </p>
                    <div className="flex gap-2">
                      <Input
                        data-testid="verse-search-input"
                        value={verseSearch}
                        onChange={(e) => setVerseSearch(e.target.value)}
                        placeholder="e.g., hope, faith, love, forgiveness..."
                        className="flex-1"
                        onKeyPress={(e) => e.key === 'Enter' && searchBibleVerse()}
                      />
                      <Button
                        type="button"
                        onClick={searchBibleVerse}
                        disabled={verseSearchLoading}
                        variant="outline"
                        className="border-teal-300 text-teal-700 hover:bg-teal-50 relative z-10"
                      >
                        {verseSearchLoading ? (
                          <div className="w-4 h-4 border-2 border-teal-300 border-t-teal-600 rounded-full animate-spin"></div>
                        ) : (
                          <Sparkles className="w-4 h-4" />
                        )}
                      </Button>
                    </div>
                    
                    {verseSearchResult && (
                      <div className="mt-3 p-3 bg-teal-50 rounded-xl border border-teal-200">
                        <p className="text-sm text-slate-800 whitespace-pre-wrap">{verseSearchResult}</p>
                        <Button
                          type="button"
                          onClick={insertVerseToPost}
                          size="sm"
                          className="mt-2 bg-teal-600 hover:bg-teal-700 text-white"
                        >
                          Add to Post
                        </Button>
                      </div>
                    )}
                  </div>

                  <Button 
                    data-testid="submit-post-btn"
                    onClick={createPost}
                    className="w-full bg-slate-800 hover:bg-slate-900 rounded-full"
                  >
                    Create Post
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Category Tabs */}
        <Tabs value={currentTab} onValueChange={(v) => setSearchParams({ tab: v })}>
          <TabsList className="bg-slate-100 p-1 rounded-full">
            <TabsTrigger value="all" className="rounded-full">All Posts</TabsTrigger>
            <TabsTrigger value="announcements" className="rounded-full">
              <Megaphone className="w-4 h-4 mr-1" />
              Announcements
            </TabsTrigger>
            <TabsTrigger value="prayer_requests" className="rounded-full">
              <Heart className="w-4 h-4 mr-1" />
              Prayer Requests
            </TabsTrigger>
            <TabsTrigger value="bible_verses" className="rounded-full">
              <BookOpen className="w-4 h-4 mr-1" />
              Bible Verses
            </TabsTrigger>
          </TabsList>

          <TabsContent value={currentTab} className="mt-6">
            {posts.length === 0 ? (
              <Card className="border-slate-100">
                <CardContent className="py-16 text-center">
                  <MessageSquare className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-600">No posts in this category yet</p>
                  <Button 
                    variant="link" 
                    className="mt-2"
                    onClick={() => setShowNewPost(true)}
                  >
                    Be the first to post
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-4">
                {posts.map((post) => {
                  const config = getCategoryConfig(post.category);
                  return (
                    <Card 
                      key={post.post_id}
                      className="border-slate-100 hover:border-slate-300 hover:shadow-lg transition-all duration-300 cursor-pointer group"
                      onClick={() => navigate(`/forum/${post.post_id}`)}
                    >
                      <CardContent className="p-6">
                        <div className="flex items-start gap-4">
                          <div className={`w-12 h-12 ${config.color} rounded-xl flex items-center justify-center flex-shrink-0`}>
                            <config.icon className="w-6 h-6 text-white" />
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <Badge variant="outline" className="text-xs">
                                {config.label}
                              </Badge>
                              {post.is_pinned && (
                                <Badge className="bg-amber-100 text-amber-800 text-xs">Pinned</Badge>
                              )}
                            </div>
                            <h3 className="font-semibold text-lg text-slate-900">
                              {post.title}
                            </h3>
                            <p className="text-slate-600 text-sm mt-1 line-clamp-2">
                              {post.content}
                            </p>
                            <div className="flex items-center gap-4 mt-3 text-xs text-slate-500">
                              <span className="flex items-center">
                                <User className="w-3 h-3 mr-1" />
                                {post.author_name}
                              </span>
                              <span>
                                {format(new Date(post.created_at), 'MMM d, yyyy')}
                              </span>
                              <span className="flex items-center">
                                <MessageSquare className="w-3 h-3 mr-1" />
                                {post.comment_count || 0} comments
                              </span>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            {canDeletePost(post) && (
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  deletePost(e, post.post_id);
                                }}
                                className="h-8 w-8 p-0 text-slate-400 hover:text-red-500 hover:bg-red-50 opacity-0 group-hover:opacity-100 relative z-10"
                                title="Delete post"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                            <ChevronRight className="w-5 h-5 text-slate-400" />
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        onConfirm={confirmDeletePost}
        title="Delete Post?"
        description="This will permanently delete this forum post. This action cannot be undone."
        confirmText="Delete Post"
      />
    </Layout>
  );
}
